"use client";

import { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWifi } from "@fortawesome/free-solid-svg-icons";

/**
 * MobileNetworkWarning Component
 * Detects slow mobile connections and warns users about potential loading issues
 */
export default function MobileNetworkWarning() {
  const [networkInfo, setNetworkInfo] = useState(null);
  const [hasShownWarning, setHasShownWarning] = useState(false);

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return;
    }

    // Check if Network Information API is available
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!connection) {
      console.log("Network Information API not available");
      return;
    }

    const checkNetworkQuality = () => {
      const effectiveType = connection.effectiveType; // '4g', '3g', '2g', 'slow-2g'
      const downlink = connection.downlink; // Mbps
      const rtt = connection.rtt; // Round-trip time in ms
      const saveData = connection.saveData; // Data saver mode

      const info = {
        effectiveType,
        downlink,
        rtt,
        saveData,
        timestamp: Date.now(),
      };

      setNetworkInfo(info);

      // Determine if connection is slow
      const isSlowConnection =
        effectiveType === "slow-2g" ||
        effectiveType === "2g" ||
        effectiveType === "3g" ||
        downlink < 1.5 || // Less than 1.5 Mbps
        rtt > 400 || // High latency
        saveData; // Data saver mode enabled

      // Show warning for slow connections (only once per session)
      if (isSlowConnection && !hasShownWarning) {
        console.warn("⚠️ Slow network detected:", info);

        // Check if we're on the onboarding page (where chunk errors are most likely)
        const isOnboarding = window.location.pathname.includes("/onboarding");

        if (isOnboarding) {
          toast({
            title: (
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faWifi} className="text-yellow-500" />
                <span>Slow Connection Detected</span>
              </div>
            ),
            description:
              "Your connection is slow. Pages may take longer to load. Please be patient.",
            variant: "warning",
            duration: 8000,
          });
        }

        setHasShownWarning(true);

        // Track slow connection for analytics
        if (typeof window !== "undefined" && window.umami) {
          window.umami.track("slow-network-detected", {
            effectiveType,
            downlink: downlink?.toFixed(2),
            rtt,
            saveData: saveData ? "true" : "false",
            page: window.location.pathname,
          });
        }
      }

      // Log good connections too for comparison
      if (!isSlowConnection) {
        console.log("✅ Good network quality:", info);
      }
    };

    // Check network quality on mount
    checkNetworkQuality();

    // Listen for network changes
    connection.addEventListener("change", checkNetworkQuality);

    // Cleanup
    return () => {
      connection.removeEventListener("change", checkNetworkQuality);
    };
  }, [hasShownWarning]);

  // Optional: Show a persistent indicator for slow connections
  const shouldShowIndicator =
    networkInfo &&
    (networkInfo.effectiveType === "slow-2g" ||
      networkInfo.effectiveType === "2g" ||
      networkInfo.downlink < 1);

  if (!shouldShowIndicator) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-[9998] bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 shadow-lg max-w-xs">
      <div className="flex items-center gap-2 text-xs text-yellow-800">
        <FontAwesomeIcon icon={faWifi} className="text-yellow-600" />
        <span className="font-medium">
          {networkInfo.effectiveType.toUpperCase()} connection
        </span>
        {networkInfo.downlink && (
          <span className="text-yellow-600">
            ({networkInfo.downlink.toFixed(1)} Mbps)
          </span>
        )}
      </div>
    </div>
  );
}

